import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Search, Plus, Handshake, Mail, Phone, MapPin, DollarSign } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// Mock data
const MOCK_BROKERS = [
  {
    id: "broker-001",
    name: "ABC Brokers",
    company: "ABC Food Brokerage Inc.",
    email: "contact@abcbrokers.com",
    phone: "(555) 123-4567",
    address: "Chicago, IL",
    commission_rate: 5,
    notes: "Strong relationships with Midwest retailers. Specializes in frozen foods category.",
    opportunity_count: 3,
    total_revenue: 1050000,
    status: "active"
  },
  {
    id: "broker-002",
    name: "XYZ Distribution",
    company: "XYZ Food Sales Group",
    email: "sales@xyzdistribution.com",
    phone: "(555) 987-6543",
    address: "Los Angeles, CA",
    commission_rate: 4.5,
    notes: "West coast coverage. Good relationships with natural food retailers.",
    opportunity_count: 2,
    total_revenue: 425000,
    status: "active"
  },
  {
    id: "broker-003",
    name: "National Food Partners",
    company: "NFP Sales Agency",
    email: "info@nfpsales.com",
    phone: "(555) 456-7890",
    address: "Atlanta, GA",
    commission_rate: 5.5,
    notes: "Southeast region specialist. Strong Publix and Kroger relationships.",
    opportunity_count: 1,
    total_revenue: 225000,
    status: "active"
  }
];

const BrokersPage = ({ navigateToOpportunity }) => {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [newBroker, setNewBroker] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    address: "",
    commission_rate: 5,
    notes: ""
  });

  useEffect(() => {
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${API}/crm/brokers`);
      setBrokers(response.data);
    } catch (error) {
      console.error('Failed to fetch brokers', error);
      setBrokers(MOCK_BROKERS);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBroker = async () => {
    try {
      await axios.post(`${API}/crm/brokers`, newBroker);
      toast.success("Broker added");
      setShowCreateModal(false);
      setNewBroker({ name: "", company: "", email: "", phone: "", address: "", commission_rate: 5, notes: "" });
      fetchBrokers();
    } catch (error) {
      console.error('Failed to create broker', error);
      toast.error("Failed to add broker");
    }
  };

  const handleViewBroker = (broker) => {
    setSelectedBroker(broker);
    setShowDetailModal(true);
  };

  const formatRevenue = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}mm`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}k`;
    return `$${amount || 0}`;
  };

  const filteredBrokers = brokers.filter(broker => {
    return searchQuery === "" || 
      broker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      broker.company?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Brokers</h1>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Handshake className="w-6 h-6" />
          Brokers
        </h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Broker
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Brokers</div>
            <div className="text-2xl font-bold">{brokers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Active Opportunities</div>
            <div className="text-2xl font-bold">
              {brokers.reduce((sum, b) => sum + (b.opportunity_count || 0), 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Pipeline via Brokers</div>
            <div className="text-2xl font-bold text-green-600">
              {formatRevenue(brokers.reduce((sum, b) => sum + (b.total_revenue || 0), 0))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search brokers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Brokers Grid */}
      <div className="grid grid-cols-2 gap-4">
        {filteredBrokers.length === 0 ? (
          <Card className="col-span-2">
            <CardContent className="p-8 text-center text-gray-500">
              No brokers found
            </CardContent>
          </Card>
        ) : (
          filteredBrokers.map(broker => (
            <Card 
              key={broker.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleViewBroker(broker)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-lg">{broker.name}</h3>
                    {broker.company && (
                      <p className="text-sm text-gray-500">{broker.company}</p>
                    )}
                  </div>
                  <Badge className={broker.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {broker.status || 'active'}
                  </Badge>
                </div>
                <div className="space-y-1 text-sm text-gray-500 mb-3">
                  {broker.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      {broker.address}
                    </div>
                  )}
                  {broker.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      {broker.email}
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-center pt-3 border-t">
                  <div className="text-xs text-gray-500">
                    <span>💼 {broker.opportunity_count || 0} opportunities</span>
                    <span className="ml-3">📊 {broker.commission_rate || 5}% commission</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">{formatRevenue(broker.total_revenue)}</div>
                    <div className="text-xs text-gray-500">pipeline</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Broker Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Broker</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Contact Name *</Label>
              <Input
                value={newBroker.name}
                onChange={(e) => setNewBroker({ ...newBroker, name: e.target.value })}
                placeholder="Primary contact name"
              />
            </div>
            <div>
              <Label>Company Name</Label>
              <Input
                value={newBroker.company}
                onChange={(e) => setNewBroker({ ...newBroker, company: e.target.value })}
                placeholder="Brokerage company name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newBroker.email}
                  onChange={(e) => setNewBroker({ ...newBroker, email: e.target.value })}
                  placeholder="email@broker.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newBroker.phone}
                  onChange={(e) => setNewBroker({ ...newBroker, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location</Label>
                <Input
                  value={newBroker.address}
                  onChange={(e) => setNewBroker({ ...newBroker, address: e.target.value })}
                  placeholder="City, State"
                />
              </div>
              <div>
                <Label>Commission Rate (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newBroker.commission_rate}
                  onChange={(e) => setNewBroker({ ...newBroker, commission_rate: parseFloat(e.target.value) || 5 })}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newBroker.notes}
                onChange={(e) => setNewBroker({ ...newBroker, notes: e.target.value })}
                placeholder="Territory, specialties, key relationships..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateBroker} disabled={!newBroker.name}>
              Add Broker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broker Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          {selectedBroker && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Handshake className="w-5 h-5" />
                  {selectedBroker.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {selectedBroker.company && (
                  <p className="text-gray-600">{selectedBroker.company}</p>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    {selectedBroker.address && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {selectedBroker.address}
                      </div>
                    )}
                    {selectedBroker.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        {selectedBroker.phone}
                      </div>
                    )}
                    {selectedBroker.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        {selectedBroker.email}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{formatRevenue(selectedBroker.total_revenue)}</div>
                    <div className="text-sm text-gray-500">Total Pipeline</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-sm text-gray-500">Commission Rate</div>
                    <div className="text-lg font-semibold">{selectedBroker.commission_rate || 5}%</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Active Opportunities</div>
                    <div className="text-lg font-semibold">{selectedBroker.opportunity_count || 0}</div>
                  </div>
                </div>

                {selectedBroker.notes && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-sm mb-1">Notes</h4>
                    <p className="text-sm text-gray-600">{selectedBroker.notes}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrokersPage;
